import { useContext } from 'react';
import { Navigate, useParams } from 'react-router';
import { Button } from 'reactstrap';

import FieldMap from '../components/field-map/FieldMap';
import Content from '../components/layout/Content';
import Header from '../components/layout/Header';
import MapToolbar from '../components/map-toolbars/MapToolbar';
import SubscriptionInfoBar from '../components/SubscriptionInfoBar';
import { useFarmCanBeEdited, useUserFarmActions } from '../utils/hooks';
import MobileFieldMap from '../components/mobile/MobileFieldMap';
import FieldsMenuContent from '../components/right-menu/FieldsMenuContent';
import SidePanel from '../components/SidePanel';
import CropPatternEditor from '../components/CropPatternEditor';
import FieldsViewContainer from '../components/field-map/FieldsViewContainer';
import { captureEvent } from '../utils/analytics';
import { useFieldsPreviewLink } from '../components/fields-preview/utils/misc';
import { MapContext } from '../contexts/MapContext';
import { mapModes } from '../constants/map';
import ButtonWithShortcut from '../components/basics/ButtonWithShortcut';
import { SHORTCUTS } from '../constants/shortcuts';
import NewTabAnchor from '../components/basics/NewTabAnchor';
import { useIsMobile } from '../utils/responsive';
import { logicalXor } from '../utils/utils';

const FieldsViewContent = ({ colors, mobileMap }) => {
  const { farmId } = useParams();
  const { canEdit } = useUserFarmActions(farmId);
  const farmCanBeEdited = useFarmCanBeEdited(farmId);
  const readonly = !canEdit || !farmCanBeEdited;

  const fieldsPreviewLink = useFieldsPreviewLink();

  const {
    mapState: { setMapMode, mapMode },
  } = useContext(MapContext);

  if (mobileMap) {
    return <MobileFieldMap colors={colors} />;
  }

  return (
    <>
      <Header showFarmButtons>
        {!canEdit ? (
          <h3>Project Map</h3>
        ) : farmCanBeEdited ? (
          <MapToolbar />
        ) : (
          <SubscriptionInfoBar />
        )}
        <div className="d-flex">
          {
            // in cases where the user cannot edit the farm, we still need to allow them to exit
            // other view-only map modes
            readonly && mapMode !== mapModes.VIEW && (
              <ButtonWithShortcut
                shortcut={SHORTCUTS.CLOSE.key}
                onClick={() => setMapMode(mapModes.VIEW)}
                size="sm"
                color="primary"
                className="mr-2"
              >
                Done
              </ButtonWithShortcut>
            )
          }
          <Button
            title="Open preview page in new tab to customize and print"
            tag={NewTabAnchor}
            size="sm"
            color="secondary"
            href={fieldsPreviewLink}
            onClick={() => {
              captureEvent('preview-page-click-from-fields-page');
            }}
          >
            Preview
          </Button>
        </div>
      </Header>
      <Content noPadding className="flex-row">
        <div className="position-relative w-100">
          <FieldMap readonly={readonly} />
        </div>
        <SidePanel right>
          <FieldsMenuContent readonly={readonly} colors={colors} />
        </SidePanel>
      </Content>
      <CropPatternEditor readonly={readonly} />
    </>
  );
};

const FieldsView = ({ mobileMap }) => {
  const isMobile = useIsMobile();
  // If necessary, redirect to the appropriate view for mobile/desktop
  if (logicalXor(mobileMap, isMobile)) {
    return <Navigate to={isMobile ? '../map' : '../fields'} />;
  }
  return (
    <FieldsViewContainer>
      {({ colors }) => (
        <FieldsViewContent mobileMap={mobileMap} colors={colors} />
      )}
    </FieldsViewContainer>
  );
};

export default FieldsView;
